import type { Database } from '@shkola/db-types';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import {
  MutationCache,
  QueryClient,
  onlineManager,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import {
  type ReactNode,
  createElement,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { type Db, must } from './db';

/**
 * Offline-first for blackouts (docs/plan.md §3 №6). Three parts:
 *   1. connectivity: a request that cannot reach the server switches the app offline, and a
 *      light probe every few seconds switches it back — no native network module needed, and it
 *      also catches «Wi-Fi without internet» when the router has no power upstream;
 *   2. an encrypted copy of what the screens showed, restored on the next start;
 *   3. a queue of actions made without connection (answers to homework, statements, messages,
 *      roll call): they wait, survive a restart, and go out by themselves; client-made ids keep
 *      a resend from creating duplicates.
 */

const NETWORK_ERROR =
  /failed to fetch|network request failed|networkerror|fetch failed|load failed/i;

/**
 * True for «the server could not be reached», not for refusals from the server — nor for a bug
 * of our own («Cannot read properties of null» is a TypeError too, and must not be retried as if
 * the connection were lost).
 */
export function isNetworkError(error: unknown): boolean {
  if (!error) return false;
  const message =
    typeof error === 'object' && 'message' in error ? String(error.message) : String(error);
  return NETWORK_ERROR.test(message);
}

let probe: ReturnType<typeof setInterval> | undefined;

function goOnline() {
  if (probe) clearInterval(probe);
  probe = undefined;
  onlineManager.setOnline(true);
}

/**
 * A fetch for the Supabase client: failures to reach the server switch the app offline; while
 * offline, `probeUrl` is tried every 5 s and the first answer switches it back online.
 */
export function connectivityFetch(probeUrl: string, headers: Record<string, string> = {}) {
  const base = globalThis.fetch.bind(globalThis);
  const goOffline = () => {
    onlineManager.setOnline(false);
    probe ??= setInterval(() => {
      base(probeUrl, { headers, cache: 'no-store' }).then(
        (res) => res.status < 500 && goOnline(),
        () => undefined,
      );
    }, 5000);
  };
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const response = await base(input, init);
      if (!onlineManager.isOnline()) goOnline();
      return response;
    } catch (error) {
      // fetch itself rejects with a TypeError only when the server cannot be reached
      if (error instanceof TypeError || isNetworkError(error)) goOffline();
      throw error;
    }
  };
}

/** Online or not, for banners. */
export function useIsOnline(): boolean {
  return useSyncExternalStore(
    (listener) => onlineManager.subscribe(listener),
    () => onlineManager.isOnline(),
    () => true,
  );
}

const WEEK = 7 * 24 * 60 * 60_000;

/** Queries that must never come from a stored copy: live, short-lived or secret. */
const NOT_STORED = new Set(['video-ticket', 'alarm', 'lesson-presence', 'mfa-factors']);

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    // a refused change (not a lost connection) is reported once, not retried forever
    mutationCache: new MutationCache(),
    defaultOptions: {
      queries: {
        // a lost connection pauses the query (the stored copy stays on screen) until it is back
        retry: (count, error) => isNetworkError(error) || count < 1,
        staleTime: 30_000,
        // the stored copy lives a week; a query must stay in memory at least as long to be stored
        gcTime: WEEK,
        refetchOnWindowFocus: false,
      },
      mutations: {
        // without connection the action waits (paused) and is retried when the app is back online
        retry: (_count, error) => isNetworkError(error),
      },
    },
  });
}

/** Encrypted storage of the platform: AES-GCM in the browser, Keychain-keyed AES on phones. */
export interface CacheStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

const CACHE_KEY = 'shkola.cache';

/** Forgets the stored copy, e.g. when somebody signs out on a shared device. */
export function forgetStoredCache(storage: CacheStorage) {
  return storage.removeItem(CACHE_KEY);
}

/**
 * Keeps the query cache (and the queue of actions made offline) in encrypted storage. After a
 * restart the screens show the last copy at once, and queued actions go out when possible.
 */
export function OfflineProvider({
  client,
  storage,
  children,
}: {
  client: QueryClient;
  storage: CacheStorage;
  children: ReactNode;
}) {
  const [persister] = useState(() =>
    createAsyncStoragePersister({ storage, key: CACHE_KEY, throttleTime: 2000 }),
  );
  return createElement(PersistQueryClientProvider, {
    client,
    persistOptions: {
      persister,
      maxAge: WEEK,
      buster: 'v1',
      dehydrateOptions: {
        shouldDehydrateQuery: (query) =>
          query.state.status === 'success' && !NOT_STORED.has(String(query.queryKey[0])),
        // paused actions (made offline) are kept until they reach the server
        shouldDehydrateMutation: (mutation) => mutation.state.isPaused,
      },
    },
    onSuccess: () => void client.resumePausedMutations(),
    children,
  });
}

// ---------------------------------------------------------------------------------------------
// The queue: actions that may be made without connection. Their functions are registered on the
// client up front, so that actions restored after a restart know how to run.
// ---------------------------------------------------------------------------------------------

export const OFFLINE_ACTIONS = {
  submitHomework: ['offline', 'homework-submit'],
  submitRequest: ['offline', 'absence-request'],
  sendMessage: ['offline', 'chat-message'],
  markCheckin: ['offline', 'alarm-checkin'],
  careOpen: ['offline', 'care-open'],
  careSend: ['offline', 'care-send'],
  setMood: ['offline', 'mood'],
  journalGrade: ['offline', 'journal', 'grade'],
  journalAbsence: ['offline', 'journal', 'absence'],
} as const;

export interface HomeworkAnswer {
  schoolId: string;
  homeworkId: string;
  courseId: string;
  studentId: string;
  body: string;
  clientId: string;
  existingId?: string;
  /** files already uploaded (uploading needs a connection; the answer itself can wait) */
  attachments?: string[];
}

export interface AbsenceStatement {
  schoolId: string;
  studentId: string;
  authorId: string;
  clientId: string;
  fromDate: string;
  toDate: string;
  reason: Database['public']['Enums']['absence_reason'];
  comment: string;
}

export interface ChatMessageDraft {
  schoolId: string;
  conversationId: string;
  authorId: string;
  body: string;
  clientId: string;
}

export interface CheckinMarks {
  schoolId: string;
  sessionId: string;
  marks: ReadonlyArray<{
    studentId: string;
    status: Database['public']['Enums']['checkin_status'];
  }>;
}

export interface CareThreadDraft {
  schoolId: string;
  kind: Database['public']['Enums']['care_kind'];
  topic: Database['public']['Enums']['care_topic'];
  body: string;
  clientId: string;
  urgent?: boolean;
  showClass?: boolean;
  showName?: boolean;
  incidentPlace?: string;
  aboutSelf?: boolean;
  reportedMessageId?: string;
}

export interface CareMessageDraft {
  threadId: string;
  body: string;
  clientId: string;
}

export interface MoodEntry {
  schoolId: string;
  mood: Database['public']['Enums']['mood'];
  note?: string;
}

// ---------------------------------------------------------------------------------------------
// The journal without connection: a mark in a lesson cell remembers what the teacher saw before
// changing it. When it reaches the server, the cell must still be as seen — otherwise somebody
// else changed it meanwhile (a substitute, the office) and the teacher decides what stays.
// ---------------------------------------------------------------------------------------------

type AssessmentKindCode = Database['public']['Enums']['assessment_kind'];
type ScaleKind = Database['public']['Enums']['grade_scale'];
type AbsenceMarkCode = Database['public']['Enums']['absence_mark'];

/** The grade columns of one value (one of them is set). */
export interface GradeColumns {
  points: number | null;
  level: string | null;
  verbal: string | null;
  passed: boolean | null;
  special: string | null;
}

export interface JournalGradeAction {
  schoolId: string;
  courseId: string;
  termId: string;
  lessonId: string;
  studentId: string;
  /** null — the grade is cleared */
  grade: {
    kind: AssessmentKindCode;
    scale: ScaleKind;
    columns: GradeColumns;
    title?: string;
  } | null;
  /** the grade on the server when the teacher changed the cell (null — there was none) */
  seen: { id: string; updatedAt: string } | null;
  /** «залишити моє» after a conflict */
  force?: boolean;
  /** when the teacher did it */
  at: string;
}

export interface JournalAbsenceAction {
  schoolId: string;
  courseId: string;
  lessonId: string;
  studentId: string;
  mark: AbsenceMarkCode | null;
  /** the mark on the server when the teacher changed it (null — present) */
  seen: AbsenceMarkCode | null;
  force?: boolean;
  at: string;
}

/** Somebody else changed the same cell while the teacher was offline. */
export class JournalConflictError extends Error {
  constructor(
    /** what is on the server now: a grade's columns, or an absence mark */
    readonly server:
      | { kind: 'grade'; grade: (GradeColumns & { kind: AssessmentKindCode }) | null }
      | { kind: 'absence'; mark: AbsenceMarkCode | null },
  ) {
    super('journal conflict');
    this.name = 'JournalConflictError';
  }
}

export const isJournalConflict = (error: unknown): error is JournalConflictError =>
  error instanceof JournalConflictError;

/**
 * What to do with a grade cell when the mark reaches the server: «apply»; «skip» — the server has
 * it already; «conflict» — somebody else changed the cell after the teacher saw it.
 */
export function judgeGradeCell(
  current: (GradeColumns & { id: string; updated_at: string; kind: AssessmentKindCode }) | null,
  a: Pick<JournalGradeAction, 'grade' | 'seen' | 'force'>,
): 'apply' | 'skip' | 'conflict' {
  const same =
    a.grade === null
      ? current === null
      : current !== null &&
        current.kind === a.grade.kind &&
        current.points === a.grade.columns.points &&
        current.level === a.grade.columns.level &&
        current.verbal === a.grade.columns.verbal &&
        current.passed === a.grade.columns.passed &&
        current.special === a.grade.columns.special;
  if (same) return 'skip';
  const changed =
    (current?.id ?? null) !== (a.seen?.id ?? null) ||
    (current !== null && a.seen !== null && current.updated_at !== a.seen.updatedAt);
  return changed && !a.force ? 'conflict' : 'apply';
}

/** The same for an absence mark. */
export function judgeAbsenceCell(
  now: AbsenceMarkCode | null,
  a: Pick<JournalAbsenceAction, 'mark' | 'seen' | 'force'>,
): 'apply' | 'skip' | 'conflict' {
  if (now === a.mark) return 'skip';
  return now !== a.seen && !a.force ? 'conflict' : 'apply';
}

async function applyJournalGrade(db: Db, a: JournalGradeAction) {
  const { data: current, error } = await db
    .from('grades')
    .select('id, updated_at, kind, scale, points, level, verbal, passed, special')
    .eq('lesson_id', a.lessonId)
    .eq('student_member_id', a.studentId)
    .maybeSingle();
  if (error) throw error;
  const verdict = judgeGradeCell(current, a);
  if (verdict === 'skip') return;
  if (verdict === 'conflict') {
    throw new JournalConflictError({
      kind: 'grade',
      grade: current && {
        kind: current.kind,
        points: current.points,
        level: current.level,
        verbal: current.verbal,
        passed: current.passed,
        special: current.special,
      },
    });
  }
  if (!a.grade) {
    if (current) must(await db.from('grades').delete().eq('id', current.id));
    return;
  }
  if (current && current.kind === a.grade.kind && current.scale === a.grade.scale) {
    must(
      await db
        .from('grades')
        .update({ ...a.grade.columns, title: a.grade.title ?? null })
        .eq('id', current.id),
    );
    return;
  }
  if (current) must(await db.from('grades').delete().eq('id', current.id));
  must(
    await db.from('grades').insert({
      school_id: a.schoolId,
      course_id: a.courseId,
      term_id: a.termId,
      student_member_id: a.studentId,
      lesson_id: a.lessonId,
      kind: a.grade.kind,
      scale: a.grade.scale,
      ...a.grade.columns,
      title: a.grade.title ?? null,
    }),
  );
}

async function applyJournalAbsence(db: Db, a: JournalAbsenceAction) {
  const { data: current, error } = await db
    .from('attendance')
    .select('mark')
    .eq('lesson_id', a.lessonId)
    .eq('student_member_id', a.studentId)
    .maybeSingle();
  if (error) throw error;
  const now = current?.mark ?? null;
  const verdict = judgeAbsenceCell(now, a);
  if (verdict === 'skip') return;
  if (verdict === 'conflict') throw new JournalConflictError({ kind: 'absence', mark: now });
  if (a.mark === null) {
    must(
      await db
        .from('attendance')
        .delete()
        .eq('lesson_id', a.lessonId)
        .eq('student_member_id', a.studentId),
    );
    return;
  }
  must(
    await db.from('attendance').upsert(
      {
        school_id: a.schoolId,
        lesson_id: a.lessonId,
        course_id: a.courseId,
        student_member_id: a.studentId,
        mark: a.mark,
      },
      { onConflict: 'lesson_id,student_member_id' },
    ),
  );
}

export function registerOfflineActions(client: QueryClient, db: Db) {
  // one after another: a teacher's marks reach the journal in the order they were made
  client.setMutationDefaults(OFFLINE_ACTIONS.journalGrade, {
    scope: { id: 'journal' },
    mutationFn: (a: JournalGradeAction) => applyJournalGrade(db, a),
    onSettled: () => client.invalidateQueries({ queryKey: ['journal'] }),
  });
  client.setMutationDefaults(OFFLINE_ACTIONS.journalAbsence, {
    scope: { id: 'journal' },
    mutationFn: (a: JournalAbsenceAction) => applyJournalAbsence(db, a),
    onSettled: () => client.invalidateQueries({ queryKey: ['journal'] }),
  });
  client.setMutationDefaults(OFFLINE_ACTIONS.submitHomework, {
    mutationFn: async (a: HomeworkAnswer) => {
      if (a.existingId) {
        return must(
          await db
            .from('homework_submissions')
            .update({ body: a.body, ...(a.attachments && { attachments: a.attachments }) })
            .eq('id', a.existingId),
        );
      }
      return must(
        await db.from('homework_submissions').upsert(
          {
            school_id: a.schoolId,
            homework_id: a.homeworkId,
            course_id: a.courseId,
            student_member_id: a.studentId,
            client_id: a.clientId,
            body: a.body,
            ...(a.attachments && { attachments: a.attachments }),
          },
          { onConflict: 'client_id', ignoreDuplicates: true },
        ),
      );
    },
    onSettled: () => client.invalidateQueries({ queryKey: ['homework'] }),
  });
  client.setMutationDefaults(OFFLINE_ACTIONS.submitRequest, {
    mutationFn: async (a: AbsenceStatement) =>
      must(
        await db.from('absence_requests').upsert(
          {
            school_id: a.schoolId,
            student_member_id: a.studentId,
            author_member_id: a.authorId,
            client_id: a.clientId,
            from_date: a.fromDate,
            to_date: a.toDate,
            reason: a.reason,
            comment: a.comment.trim() || null,
          },
          { onConflict: 'client_id', ignoreDuplicates: true },
        ),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ['absence-requests'] }),
  });
  client.setMutationDefaults(OFFLINE_ACTIONS.sendMessage, {
    // one after another, so the messages arrive in the order they were written
    scope: { id: 'chat' },
    mutationFn: async (a: ChatMessageDraft) =>
      must(
        await db.from('messages').upsert(
          {
            school_id: a.schoolId,
            conversation_id: a.conversationId,
            author_member_id: a.authorId,
            body: a.body,
            client_id: a.clientId,
          },
          { onConflict: 'client_id', ignoreDuplicates: true },
        ),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ['chats'] }),
  });
  client.setMutationDefaults(OFFLINE_ACTIONS.markCheckin, {
    mutationFn: async (a: CheckinMarks) =>
      must(
        await db.from('alarm_checkins').upsert(
          a.marks.map((m) => ({
            school_id: a.schoolId,
            session_id: a.sessionId,
            student_member_id: m.studentId,
            status: m.status,
          })),
          { onConflict: 'session_id,student_member_id' },
        ),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ['alarm'] }),
  });
  client.setMutationDefaults(OFFLINE_ACTIONS.careOpen, {
    // a thread first, then the messages written into it
    scope: { id: 'care' },
    mutationFn: async (a: CareThreadDraft) =>
      must(
        await db.rpc('care_open', {
          p_school: a.schoolId,
          p_kind: a.kind,
          p_topic: a.topic,
          p_body: a.body,
          p_client_id: a.clientId,
          p_urgent: a.urgent ?? false,
          p_show_class: a.showClass ?? false,
          p_show_name: a.showName ?? false,
          ...(a.incidentPlace !== undefined && { p_incident_place: a.incidentPlace }),
          ...(a.aboutSelf !== undefined && { p_about_self: a.aboutSelf }),
          ...(a.reportedMessageId !== undefined && { p_reported_message: a.reportedMessageId }),
        }),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ['care'] }),
  });
  client.setMutationDefaults(OFFLINE_ACTIONS.careSend, {
    scope: { id: 'care' },
    mutationFn: async (a: CareMessageDraft) =>
      must(
        await db.rpc('care_send', {
          p_thread: a.threadId,
          p_body: a.body,
          p_client_id: a.clientId,
        }),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ['care'] }),
  });
  client.setMutationDefaults(OFFLINE_ACTIONS.setMood, {
    mutationFn: async (a: MoodEntry) =>
      must(
        await db.rpc('set_mood', {
          p_school: a.schoolId,
          p_mood: a.mood,
          ...(a.note !== undefined && { p_note: a.note }),
        }),
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ['care', 'mood'] }),
  });
}

/** Actions of one kind still waiting for the connection (for «очікує звʼязку» marks). */
export function useQueuedActions<T>(key: readonly string[]): T[] {
  return useMutationState({
    filters: { mutationKey: key, predicate: (m) => m.state.isPaused },
    select: (m) => m.state.variables as T,
  });
}

/**
 * When the newest data on screen came from the server (for «оновлено о …»). Read while drawing,
 * not subscribed: offline nothing new arrives, and a subscription would fire while other
 * components draw.
 */
export function useLastSync(): number | null {
  const client = useQueryClient();
  useIsOnline();
  const newest = Math.max(
    0,
    ...client
      .getQueryCache()
      .getAll()
      .map((q) => q.state.dataUpdatedAt),
  );
  return newest || null;
}

/** How many actions wait for the connection, of every kind. */
export function useQueuedCount(): number {
  return useMutationState({ filters: { predicate: (m) => m.state.isPaused } }).length;
}

/**
 * What the offline banner says: nothing while online (except a short «back» after a blackout),
 * otherwise since when the data is and how much waits to be sent.
 */
export function useConnectionBanner():
  { kind: 'offline'; lastSync: number | null; queued: number } | { kind: 'back' } | null {
  const online = useIsOnline();
  const lastSync = useLastSync();
  const queued = useQueuedCount();
  const [back, setBack] = useState(false);
  const wasOffline = useRef(false);
  useEffect(() => {
    if (!online) wasOffline.current = true;
    else if (wasOffline.current) {
      wasOffline.current = false;
      setBack(true);
    }
  }, [online]);
  useEffect(() => {
    if (!back) return;
    const id = setTimeout(() => setBack(false), 4000);
    return () => clearTimeout(id);
  }, [back]);
  if (!online) return { kind: 'offline', lastSync, queued };
  return back ? { kind: 'back' } : null;
}
